import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Input from '../ui/Input';

/**
 * BiometricSettingsModal - Modal for configuring biometric verification per recipient
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {Function} props.onClose - Called when modal closes
 * @param {Function} props.onSave - Called when settings are saved
 * @param {Object} props.recipient - Recipient object
 * @param {Object} props.initialSettings - Initial verification settings
 */
function BiometricSettingsModal({
  isOpen,
  onClose,
  onSave,
  recipient,
  initialSettings = {}
}) {
  const { t } = useTranslation();
  
  const [settings, setSettings] = useState({
    enabled: initialSettings.enabled || false,
    threshold: initialSettings.threshold || 85,
    requireDocumentBack: initialSettings.requireDocumentBack === undefined ? true : initialSettings.requireDocumentBack,
    notes: initialSettings.notes || ''
  });

  // Update settings when initialSettings or recipient changes
  useEffect(() => {
    setSettings({
      enabled: initialSettings.enabled || false,
      threshold: initialSettings.threshold || 85,
      requireDocumentBack: initialSettings.requireDocumentBack === undefined ? true : initialSettings.requireDocumentBack,
      notes: initialSettings.notes || ''
    });
  }, [initialSettings, recipient, isOpen]);

  const handleSave = () => {
    onSave(settings);
    onClose();
  };

  const handleCancel = () => {
    // Reset to initial settings
    setSettings({
      enabled: initialSettings.enabled || false,
      threshold: initialSettings.threshold || 85,
      requireDocumentBack: initialSettings.requireDocumentBack === undefined ? true : initialSettings.requireDocumentBack,
      notes: initialSettings.notes || ''
    });
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title={t('identity-settings-title', {
        defaultValue: 'Biometric verification for {{name}}',
        name: recipient?.name || recipient?.email || 'Signer'
      })}
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          {t('identity-settings-description', {
            defaultValue: 'Bloquea a este firmante hasta que suba su documento de identidad y pase la verificación biométrica.'
          })}
        </p>

        {/* Enable/Disable Toggle */}
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="biometric-enabled"
            checked={settings.enabled}
            onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
            className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <label htmlFor="biometric-enabled" className="flex-1 text-sm">
            <span className="font-medium text-gray-900">
              {t('identity-settings-enable', {
                defaultValue: 'Require identity verification before signing'
              })}
            </span>
          </label>
        </div>

        {settings.enabled && (
          <>
            {/* Threshold Input */}
            <div>
              <label htmlFor="biometric-threshold" className="block text-sm font-medium text-gray-700 mb-1">
                {t('identity-settings-threshold', {
                  defaultValue: 'Minimum match (%)'
                })}
              </label>
              <Input
                id="biometric-threshold"
                type="number"
                min="0"
                max="100"
                value={settings.threshold}
                onChange={(e) => {
                  const value = Math.max(0, Math.min(100, Number(e.target.value) || 85));
                  setSettings({ ...settings, threshold: value });
                }}
                className="w-full"
              />
              <p className="mt-1 text-xs text-gray-500">
                {t('identity-verification-threshold', {
                  defaultValue: 'Minimum required match: {{value}}% similarity.',
                  value: settings.threshold
                })}
              </p>
            </div>

            {/* Require Document Back Checkbox */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="require-document-back"
                checked={settings.requireDocumentBack}
                onChange={(e) => setSettings({ ...settings, requireDocumentBack: e.target.checked })}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <label htmlFor="require-document-back" className="flex-1 text-sm">
                <span className="font-medium text-gray-900">
                  {t('identity-settings-require-document-back', {
                    defaultValue: 'Require back side of ID document'
                  })}
                </span>
                <p className="mt-1 text-xs text-gray-500">
                  {t('identity-settings-require-document-back-help', {
                    defaultValue: 'If unchecked, only the front side of the ID will be required.'
                  })}
                </p>
              </label>
            </div>

            {/* Notes Input */}
            <div>
              <label htmlFor="biometric-notes" className="block text-sm font-medium text-gray-700 mb-1">
                {t('identity-settings-notes', {
                  defaultValue: 'Reviewer notes (optional)'
                })}
              </label>
              <textarea
                id="biometric-notes"
                value={settings.notes}
                onChange={(e) => setSettings({ ...settings, notes: e.target.value })}
                placeholder={t('identity-settings-notes-placeholder', {
                  defaultValue: 'Only your team can see this note.'
                })}
                rows={3}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
          </>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button
            type="button"
            variant="ghost"
            onClick={handleCancel}
          >
            {t('cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSave}
          >
            {settings.enabled
              ? t('identity-settings-save', { defaultValue: 'Save requirement' })
              : t('identity-settings-disable', { defaultValue: 'Leave disabled' })}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default BiometricSettingsModal;
